import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { BillingPeriod } from '../../../clients.models';
import { env } from '../../../../../env/env';

const POLISH_DATE_FORMATS = {
  parse: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' }
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' }
  }
};

@Component({
  selector: 'settlement-dialog',
  templateUrl: './settlement-dialog.html',
  styleUrl: './settlement-dialog.css',
  imports: [
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: POLISH_DATE_FORMATS }
  ]
})
export class SettlementDialog {
  readonly data = inject<{ clientId: string, billingPeriodFrom: Date | string | null }>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SettlementDialog>);
  private readonly dateAdapter = inject(DateAdapter<Date>);
  private readonly httpClient = inject(HttpClient);

  public readonly today: Date = new Date();
  public billingPeriodFrom: Date | null = this.toDate(this.data.billingPeriodFrom);
  public billingPeriodTo: Date | null = null;
  public newBillingPeriodFrom: Date | null = null;
  public newBillingPeriodTo: Date | null = null;

  public errors: {
    billingPeriodTo: string | null,
    newBillingPeriodFrom: string | null,
    newBillingPeriodTo: string | null
  } = { billingPeriodTo: null, newBillingPeriodFrom: null, newBillingPeriodTo: null };

  public ngOnInit(): void {
    this.dateAdapter.setLocale('pl-PL');
  }

  public closeDialog(): void {
    this.dialogRef.close(false);
  }

  public isValid(): boolean {
    this.errors = { billingPeriodTo: null, newBillingPeriodFrom: null, newBillingPeriodTo: null };

    if (!this.billingPeriodFrom || !this.billingPeriodTo || !this.newBillingPeriodFrom || !this.newBillingPeriodTo) {
      return false;
    }

    if (this.billingPeriodTo > this.today) {
      this.errors.billingPeriodTo = 'Data nie może być późniejsza niż dziś';
      return false;
    }

    if (this.billingPeriodTo < this.billingPeriodFrom) {
      this.errors.billingPeriodTo = 'Data nie może być wcześniejsza niż data od';
      return false;
    }

    if (this.newBillingPeriodFrom < this.billingPeriodTo) {
      this.errors.newBillingPeriodFrom = 'Data nie może być wcześniejsza niż koniec okresu rozliczenia';
      return false;
    }

    if (this.newBillingPeriodTo < this.newBillingPeriodFrom) {
      this.errors.newBillingPeriodTo = 'Data nie może być wcześniejsza niż data od';
      return false;
    }

    return true;
  }

  public settle(): void {
    if (!this.isValid()) {
      return;
    }

    this.httpClient.post(`${env.apiUrl}/clients/${this.data.clientId}/settle`, {
      settlePeriod: {
        from: this.formatDate(this.billingPeriodFrom),
        to: this.formatDate(this.billingPeriodTo)
      },
      newBillingPeriod: {
        from: this.formatDate(this.newBillingPeriodFrom),
        to: this.formatDate(this.newBillingPeriodTo)
      }
    }).subscribe({
      next: (response) => {
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.dialogRef.close(false);
      }
    });
  }

  private formatDate(date: Date | null): string {
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toDate(value: Date | string | null): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  }
}
